import React, {Fragment, useEffect, useRef, useState} from "react";
import {AccountCircle, ArrowDropDown} from "@mui/icons-material";
import {DialogActions, DialogContent, IconButton, Menu, MenuItem, Tooltip} from "@mui/material";
import Button from "@mui/material/Button";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import CustomDialog from "../custom-dialog";
import CustomDialogTitle from "../custom-dialog-title";
import UserSettingsForm from "../settings-select/users-settings/user-settings-form";
import EditUserForm from "./edit-user-form";
import UserChangePasswordForm from "./user-change-password-form";
import {hubStateSelector} from "../../state/hub/hubSelectors";
import {
  fetchExternalUser,
  hideUserChangePasswordForm,
  hideUserEditUserModal,
  hideUserRegisterModal,
  logoutExternalUser,
  logoutUser,
  showUserChangePasswordForm,
  showUserEditUserModal,
  showUserLoginForm
} from "../../state/user/userActions";
import {BASE_LOGIN_IDENTIFIER} from "../../state/user/userReducer";
import {userSelector} from "../../state/user/userSelectors";
import {HEADER_BUTTON_VIEW_MODE_ICON, HEADER_BUTTON_VIEW_MODE_ICONTEXT} from "../header/utils";

let providerId = null;

const UserSelect = ({disabled, viewMode}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const user = useSelector(userSelector);
  const supportedAuths = useSelector(hubStateSelector)?.supportedAuths;

  const loginPopupRef = useRef();

  const registerFormRef = useRef();
  const changePasswordFormRef = useRef();
  const editUserFormRef = useRef();

  const [loginAnchorEl, setLoginAnchorEl] = useState(null);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);

  useEffect(() => {
    window.onstorage = e => {
      const loginCode = localStorage.getItem("loginCode");
      if (e.storageArea === localStorage && loginCode && providerId) {
        dispatch(fetchExternalUser(providerId, loginCode));
        localStorage.removeItem("loginCode");
        providerId = null;
        loginPopupRef.current.close();
      }
    };
    return () => {
      window.onstorage = null;
    };
  }, [dispatch]);

  const handleLoginOpen = event => {
    setLoginAnchorEl(event.currentTarget);
  };
  const handleLoginClose = () => {
    setLoginAnchorEl(null);
  };

  const handleProfileOpen = event => {
    setProfileAnchorEl(event.currentTarget);
  };
  const handleProfileClose = () => {
    setProfileAnchorEl(null);
  };

  const handleExternalLogin = (provider, loginURL) => {
    providerId = provider;
    localStorage.removeItem("loginCode");
    loginPopupRef.current = window.open(loginURL, "_blank", "width=720,height=720");
  };

  const handleLogout = () => {
    const currentAuth = (supportedAuths ?? []).find(({identifier}) => identifier === user.provider);
    if (!currentAuth?.identifier || currentAuth.identifier === BASE_LOGIN_IDENTIFIER) {
      dispatch(logoutUser(user.userId));
    } else {
      dispatch(logoutExternalUser(user.userId));
    }
  };

  return (
    <Fragment>
      {user && user.isAuthenticated ? (
        <>
          <Tooltip title={user.email}>
            {viewMode === HEADER_BUTTON_VIEW_MODE_ICON ? (
              <IconButton
                onClick={handleProfileOpen}
                aria-label={t("components.userSelect.profile.ariaLabel")}
                color="inherit"
              >
                <AccountCircle />
              </IconButton>
            ) : (
              <Button
                onClick={handleProfileOpen}
                startIcon={viewMode === HEADER_BUTTON_VIEW_MODE_ICONTEXT ? <AccountCircle /> : null}
                endIcon={<ArrowDropDown />}
                aria-label={t("components.userSelect.profile.ariaLabel")}
                classes={{
                  endIcon: "button-select__menu-button__end-icon"
                }}
                sx={{
                  background: Boolean(profileAnchorEl) ? "#f5f5f5" : undefined
                }}
              >
                {t("components.userSelect.profile.label")}
              </Button>
            )}
          </Tooltip>
          <Menu
            open={Boolean(profileAnchorEl)}
            anchorEl={profileAnchorEl}
            anchorOrigin={{
              vertical: "top",
              horizontal: "left"
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right"
            }}
            onClose={handleProfileClose}
            keepMounted
          >
            {user.provider !== BASE_LOGIN_IDENTIFIER ? null : (
              <MenuItem
                key="changePassword"
                id="user-select__change-password-btn"
                onClick={() => {
                  dispatch(showUserChangePasswordForm());
                  handleProfileClose();
                }}
              >
                {t("components.userSelect.profile.menuItems.changePassword")}
              </MenuItem>
            )}
            {user.provider !== BASE_LOGIN_IDENTIFIER ? null : (
              <MenuItem
                key="editUser"
                id="user-select__edit-user-btn"
                onClick={() => {
                  dispatch(showUserEditUserModal());
                  handleProfileClose();
                }}
              >
                {t("components.userSelect.profile.menuItems.editUser")}
              </MenuItem>
            )}
            <MenuItem
              key="logout"
              id="user-select__logout-btn"
              onClick={() => {
                handleLogout();
                handleProfileClose();
              }}
            >
              {t("components.userSelect.profile.menuItems.logout")}
            </MenuItem>
          </Menu>
        </>
      ) : (supportedAuths || []).length === 1 ? (
        <Tooltip title={t("components.userSelect.login.title")}>
          <Button
            onClick={() =>
              supportedAuths[0].identifier === BASE_LOGIN_IDENTIFIER
                ? dispatch(showUserLoginForm())
                : handleExternalLogin(supportedAuths[0].identifier, supportedAuths[0].loginURL)
            }
            startIcon={viewMode === HEADER_BUTTON_VIEW_MODE_ICONTEXT ? <AccountCircle /> : null}
            aria-label={t("components.userSelect.login.ariaLabel")}
            variant="text"
          >
            {t("components.userSelect.login.label")}
          </Button>
        </Tooltip>
      ) : (
        <>
          <Tooltip title={t("components.userSelect.login.title")}>
            <Button
              onClick={handleLoginOpen}
              startIcon={viewMode === HEADER_BUTTON_VIEW_MODE_ICONTEXT ? <AccountCircle /> : null}
              aria-label={t("components.userSelect.login.ariaLabel")}
              sx={{
                background: Boolean(profileAnchorEl) ? "#f5f5f5" : "white"
              }}
              variant="text"
            >
              {t("components.userSelect.login.label")}
            </Button>
          </Tooltip>
          <Menu
            open={Boolean(loginAnchorEl)}
            anchorEl={loginAnchorEl}
            anchorOrigin={{
              vertical: "top",
              horizontal: "left"
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right"
            }}
            onClose={handleLoginClose}
            keepMounted
          >
            {supportedAuths.map(({identifier, label, loginURL}) => (
              <MenuItem
                key={identifier}
                onClick={() => {
                  if (identifier === BASE_LOGIN_IDENTIFIER) {
                    dispatch(showUserLoginForm());
                  } else {
                    handleExternalLogin(identifier, loginURL);
                  }
                  handleLoginClose();
                }}
              >
                {identifier === BASE_LOGIN_IDENTIFIER
                  ? t("components.userSelect.login.menuItems.loginWithBasicAuth")
                  : t("components.userSelect.login.menuItems.loginWith", {provider: label || identifier})}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}

      <CustomDialog
        open={user.isRegisterDialogOpen && !disabled}
        onClose={() => {
          if (registerFormRef.current) {
            registerFormRef.current.cancel(() => {
              dispatch(hideUserRegisterModal());
            });
          } else {
            dispatch(hideUserRegisterModal());
          }
        }}
      >
        <CustomDialogTitle
          onClose={() => {
            if (registerFormRef.current) {
              registerFormRef.current.cancel(() => {
                dispatch(hideUserRegisterModal());
              });
            } else {
              dispatch(hideUserRegisterModal());
            }
          }}
        >
          {t("components.userSelect.dialog.register.title")}
        </CustomDialogTitle>
        <DialogContent>
          <UserSettingsForm ref={registerFormRef} userId={-1} isAnonymous />
        </DialogContent>
        <DialogActions>
          <Button
            className="components__dialog__btn components__dialog__btn--cancel"
            onClick={() => {
              if (registerFormRef.current) {
                registerFormRef.current.cancel(() => {
                  dispatch(hideUserRegisterModal());
                });
              } else {
                dispatch(hideUserRegisterModal());
              }
            }}
          >
            {t("commons.confirm.cancel")}
          </Button>
          <Button
            className="components__dialog__btn components__dialog__btn--submit"
            onClick={() => {
              if (registerFormRef.current) {
                registerFormRef.current.submit(() => {});
              }
            }}
          >
            {t("components.userSelect.dialog.register.actions.submit")}
          </Button>
        </DialogActions>
      </CustomDialog>

      <CustomDialog
        open={user.isEditUserDialogOpen && !disabled}
        onClose={() => {
          if (editUserFormRef.current) {
            editUserFormRef.current.cancel(() => {
              dispatch(hideUserEditUserModal());
            });
          } else {
            dispatch(hideUserEditUserModal());
          }
        }}
      >
        <CustomDialogTitle
          onClose={() => {
            if (editUserFormRef.current) {
              editUserFormRef.current.cancel(() => {
                dispatch(hideUserEditUserModal());
              });
            } else {
              dispatch(hideUserEditUserModal());
            }
          }}
        >
          {t("components.userSelect.dialog.edituser.title")}
        </CustomDialogTitle>
        <DialogContent>
          <EditUserForm ref={editUserFormRef} />
        </DialogContent>
        <DialogActions>
          <Button
            className="components__dialog__btn components__dialog__btn--cancel"
            onClick={() => {
              if (editUserFormRef.current) {
                editUserFormRef.current.cancel(() => {
                  dispatch(hideUserEditUserModal());
                });
              } else {
                dispatch(hideUserEditUserModal());
              }
            }}
          >
            {t("commons.confirm.cancel")}
          </Button>
          <Button
            className="components__dialog__btn components__dialog__btn--submit"
            onClick={() => {
              if (editUserFormRef.current) {
                editUserFormRef.current.submit(() => {});
              }
            }}
          >
            {t("commons.confirm.submit")}
          </Button>
        </DialogActions>
      </CustomDialog>

      <CustomDialog
        open={user.isChangePasswordDialogOpen && !disabled}
        onClose={() => {
          if (changePasswordFormRef.current) {
            changePasswordFormRef.current.cancel(() => {
              dispatch(hideUserChangePasswordForm());
            });
          } else {
            dispatch(hideUserChangePasswordForm());
          }
        }}
      >
        <CustomDialogTitle
          onClose={() => {
            if (changePasswordFormRef.current) {
              changePasswordFormRef.current.cancel(() => {
                dispatch(hideUserChangePasswordForm());
              });
            } else {
              dispatch(hideUserChangePasswordForm());
            }
          }}
        >
          {t("components.userSelect.dialog.changePassword.title")}
        </CustomDialogTitle>
        <DialogContent>
          <UserChangePasswordForm ref={changePasswordFormRef} />
        </DialogContent>
        <DialogActions>
          <Button
            className="components__dialog__btn components__dialog__btn--cancel"
            onClick={() => {
              if (changePasswordFormRef.current) {
                changePasswordFormRef.current.cancel(() => {
                  dispatch(hideUserChangePasswordForm());
                });
              } else {
                dispatch(hideUserChangePasswordForm());
              }
            }}
          >
            {t("commons.confirm.cancel")}
          </Button>
          <Button
            className="components__dialog__btn components__dialog__btn--submit"
            onClick={() => {
              if (changePasswordFormRef.current) {
                changePasswordFormRef.current.submit(() => {});
              }
            }}
          >
            {t("commons.confirm.submit")}
          </Button>
        </DialogActions>
      </CustomDialog>
    </Fragment>
  );
};

export default UserSelect;
